// React Query hooks for the generic API: schema, list/get/create/update/archive/restore/revert, history, links,
// comments (Notes) and global search. Cache keys start with ['records', entityName] so one invalidation refreshes
// every list, record, history and ref lookup of that entity.
import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { api } from './api'
import { useAuth } from './auth-context'
import {
  type ListParams,
  type ListResult,
  listQuery,
  normalizeHistory,
  normalizeLinkGroups,
  normalizeList,
  normalizeRecord,
  normalizeSearch,
  paths,
  type RecordSummary,
  revertBody,
} from './endpoints'
import { displayOf } from './fields'
import { entityAccess } from './permissions'
import { type EntitySchema, normalizeSchema, type RecordRow, SchemaIndex } from './schema'

export const qk = {
  schema: ['schema'] as const,
  records: (entity: string) => ['records', entity] as const,
  list: (entity: string, params: ListParams) => ['records', entity, 'list', params] as const,
  record: (entity: string, id: string) => ['records', entity, 'one', id] as const,
  byIds: (entity: string, ids: string[]) => ['records', entity, 'byIds', ids] as const,
  history: (entity: string, id: string) => ['records', entity, 'history', id] as const,
  links: ['links'] as const,
  recordLinks: (entity: string, id: string) => ['links', entity, id] as const,
  comments: (entity: string, id: string) => ['records', 'comment', 'target', entity, id] as const,
  search: (q: string) => ['search', q] as const,
}

// ------------------------------------------------------------------ schema

export const schemaQueryOptions = queryOptions({
  queryKey: qk.schema,
  queryFn: async ({ signal }) => new SchemaIndex(normalizeSchema(await api.get(paths.schema, { signal }))),
  staleTime: 10 * 60_000,
})

export function useSchema() {
  return useQuery(schemaQueryOptions)
}

// ------------------------------------------------------------------ reads

export function useEntityList(entity: EntitySchema, params: ListParams, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: qk.list(entity.name, params),
    queryFn: async ({ signal }) =>
      normalizeList(await api.get(paths.list(entity.plural), { query: listQuery(params), signal })),
    placeholderData: keepPreviousData,
    enabled: opts.enabled ?? true,
  })
}

/** Fetches every page of a filtered list (CSV export). Stops at maxRows. */
export async function fetchAllPages(
  entity: EntitySchema,
  params: ListParams,
  maxRows = 10_000,
): Promise<{ rows: RecordRow[]; total: number; truncated: boolean }> {
  const rows: RecordRow[] = []
  let cursor: string | null = null
  let total = 0
  do {
    const page: ListResult = normalizeList(
      await api.get(paths.list(entity.plural), { query: listQuery({ ...params, limit: 500, cursor }) }),
    )
    rows.push(...page.data)
    total = page.total
    cursor = page.nextCursor
  } while (cursor && rows.length < maxRows)
  return { rows: rows.slice(0, maxRows), total, truncated: rows.length < total }
}

/** Reference and file fields of an entity (what expand= accepts). */
export function refFields(entity: EntitySchema): string[] {
  return entity.fields
    .filter((f) => f.kind === 'ref' || f.kind === 'refs' || f.kind === 'file')
    .map((f) => f.name)
}

/** One record by id or key, with every reference field expanded to { id, display } summaries. */
export function useRecord(entity: EntitySchema, id: string) {
  return useQuery({
    queryKey: qk.record(entity.name, id),
    queryFn: async ({ signal }) => {
      const expand = refFields(entity)
      return normalizeRecord(
        await api.get(paths.record(entity.plural, id), {
          query: expand.length ? { expand } : undefined,
          signal,
        }),
      )
    },
  })
}

export function useHistory(entity: EntitySchema, id: string, enabled = true) {
  return useQuery({
    queryKey: qk.history(entity.name, id),
    queryFn: async ({ signal }) =>
      normalizeHistory(await api.get(paths.history(entity.plural, id), { signal })),
    enabled,
  })
}

const BATCH = 100

async function fetchByIds(entity: EntitySchema, ids: string[], signal?: AbortSignal): Promise<RecordRow[]> {
  const out: RecordRow[] = []
  for (let i = 0; i < ids.length; i += BATCH) {
    const chunk = ids.slice(i, i + BATCH)
    const page = normalizeList(
      await api.get(paths.list(entity.plural), {
        query: { id__in: chunk, limit: chunk.length, include_archived: 'true' },
        signal,
      }),
    )
    out.push(...page.data)
  }
  return out
}

/** Rows of one entity by id (for mapped records and ref pickers). */
export function useRecordsByIds(entity: EntitySchema | undefined, ids: string[]) {
  const sorted = useMemo(() => [...new Set(ids)].sort(), [ids])
  return useQuery({
    queryKey: qk.byIds(entity?.name ?? '_', sorted),
    queryFn: ({ signal }) => fetchByIds(entity!, sorted, signal),
    enabled: !!entity && sorted.length > 0,
    staleTime: 60_000,
  })
}

/**
 * Resolves ref ids to summaries (history diffs and other places without expand=). refIds: { targetEntityName: ids[] }.
 * Targets the viewer cannot read are skipped (the value then shows a short id). Returns (entityName, id) => summary.
 */
export function useRefDisplays(schema: SchemaIndex | undefined, refIds: Record<string, string[]>) {
  const { viewer } = useAuth()
  const targets = useMemo(
    () =>
      Object.entries(refIds)
        .map(([name, ids]) => ({ entity: schema?.entity(name), ids }))
        .filter(
          (t): t is { entity: EntitySchema; ids: string[] } =>
            !!t.entity && t.ids.length > 0 && entityAccess(viewer, t.entity).read,
        ),
    [schema, refIds, viewer],
  )
  const data = useQueries({
    queries: targets.map((t) => ({
      queryKey: qk.byIds(t.entity.name, t.ids),
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchByIds(t.entity, t.ids, signal),
      staleTime: 60_000,
    })),
    combine: (results) => results.map((r) => r.data),
  })
  const maps = useMemo(() => {
    const m = new Map<string, Map<string, RecordSummary>>()
    targets.forEach((t, i) => {
      const rows = data[i] ?? []
      m.set(
        t.entity.name,
        new Map(
          rows.map((r) => [
            r.id,
            {
              id: r.id,
              entity: t.entity.name,
              plural: t.entity.plural,
              display: displayOf(t.entity, r),
              archived: !!r.archivedAt,
            },
          ]),
        ),
      )
    })
    return m
  }, [targets, data])
  return useCallback((entityName: string, id: string) => maps.get(entityName)?.get(id), [maps])
}

// ------------------------------------------------------------------ writes

export function useCreateRecord(entity: EntitySchema) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { data: Record<string, unknown>; reason?: string }) =>
      api.post(paths.list(entity.plural), input.data, { reason: input.reason }).then(normalizeRecord),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.records(entity.name) }),
    meta: { silent: [400] },
  })
}

export interface UpdateInput {
  id: string
  patch: Record<string, unknown>
  /** Version the edit is based on (If-Match). */
  version?: number
  reason?: string
}

export function useUpdateRecord(entity: EntitySchema) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateInput) =>
      api
        .patch(paths.record(entity.plural, input.id), input.patch, {
          ifMatch: input.version,
          reason: input.reason,
        })
        .then(normalizeRecord),
    onSuccess: (row, input) => {
      // Keep the expanded reference summaries until the refetch replaces them, so the new version is usable at once.
      const merge = (old: RecordRow | undefined) => ({ ...row, _expanded: old?._expanded })
      qc.setQueryData<RecordRow>(qk.record(entity.name, input.id), merge)
      if (input.id !== row.id) qc.setQueryData<RecordRow>(qk.record(entity.name, row.id), merge)
      return qc.invalidateQueries({ queryKey: qk.records(entity.name) })
    },
    meta: { silent: [400, 409] },
  })
}

function useRecordAction(entity: EntitySchema, action: 'archive' | 'restore') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; reason?: string }) =>
      api
        .post(paths.action(entity.plural, input.id, action), {}, { reason: input.reason })
        .then(normalizeRecord),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.records(entity.name) }),
  })
}

export const useArchiveRecord = (entity: EntitySchema) => useRecordAction(entity, 'archive')
export const useRestoreRecord = (entity: EntitySchema) => useRecordAction(entity, 'restore')

export function useRevertRecord(entity: EntitySchema) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; version: number; reason?: string }) =>
      api
        .post(paths.action(entity.plural, input.id, 'revert'), revertBody(input.version), {
          reason: input.reason,
        })
        .then(normalizeRecord),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.records(entity.name) }),
  })
}

// ------------------------------------------------------------------ links

/** Every link group of a record (Mapped records tab). */
export function useRecordLinks(entity: EntitySchema, id: string, enabled = true) {
  return useQuery({
    queryKey: qk.recordLinks(entity.name, id),
    queryFn: async ({ signal }) =>
      normalizeLinkGroups(await api.get(paths.recordLinks(entity.plural, id), { signal })),
    enabled,
  })
}

export interface LinkInput {
  link: string
  fromId: string
  toId: string
  reason?: string
}

/**
 * Map or unmap through POST / DELETE /api/v1/links/<link>. Silent for the global error toast: every caller (Mapped
 * records tab, module panels) toasts the failure itself with the server's message, which for a 400 link_rejected
 * explains why the link is refused. Without it each failure showed two identical toasts.
 */
export function linkMutationOptions(qc: QueryClient, op: 'link' | 'unlink') {
  return {
    mutationFn: (input: LinkInput) =>
      op === 'link'
        ? api.post(
            paths.links(input.link),
            { fromId: input.fromId, toId: input.toId },
            { reason: input.reason },
          )
        : api.del(
            paths.links(input.link),
            { fromId: input.fromId, toId: input.toId },
            { reason: input.reason },
          ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.links }),
    meta: { silent: true },
  }
}

export function useLinkMutation(op: 'link' | 'unlink') {
  const qc = useQueryClient()
  return useMutation(linkMutationOptions(qc, op))
}

// ------------------------------------------------------------------ notes

export function useComments(entity: EntitySchema, id: string, enabled = true) {
  return useQuery({
    queryKey: qk.comments(entity.name, id),
    queryFn: async ({ signal }) =>
      normalizeList(await api.get(paths.comments(entity.plural, id), { query: { limit: 200 }, signal })),
    enabled,
  })
}

export function useAddComment(entity: EntitySchema, id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { body: string }) =>
      api.post(paths.comments(entity.plural, id), { body: input.body }).then(normalizeRecord),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.records('comment') }),
  })
}

// ------------------------------------------------------------------ search

export function useGlobalSearch(q: string) {
  const term = q.trim()
  return useQuery({
    queryKey: qk.search(term),
    queryFn: async ({ signal }) =>
      normalizeSearch(await api.get(paths.search, { query: { q: term, limit: 5 }, signal })),
    enabled: term.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  })
}
